import * as React from 'react';
import Accordion from '@mui/material/Accordion';
import AccordionDetails from '@mui/material/AccordionDetails';
import AccordionSummary from '@mui/material/AccordionSummary';
import Container from '@mui/material/Container';
import Link from '@mui/material/Link';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';

const faqs = [
  {
    question: 'How do I get support if I run into an issue?',
    answer:
      'Email support@algo-trader.tech or reach out through the app. The support path is meant to be direct, not hidden behind a forum.',
  },
  {
    question: 'Where do my funds sit when I use AlgoTrader?',
    answer:
      'Your funds remain on your exchange account. AlgoTrader connects through your API setup rather than holding custody of your crypto.',
  },
  {
    question: 'Which exchanges can I connect?',
    answer:
      'AlgoTrader supports connecting to Gate.io, Kraken, Coinbase, Binance, Binance.US, and Binance TestNet.',
  },
  {
    question: 'What indicators are available?',
    answer:
      'The platform supports common technical tools including MACD, RSI, EMA crossover, MFI, ADX, SMA, and more depending on the strategy flow you choose.',
  },
  {
    question: 'Why is there a paid plan if the mobile app is free?',
    answer:
      'The subscription unlocks the full web dashboard experience. Mobile remains free, while web access gives you the denser command-center workflow shown in the new product screenshots.',
  },
  {
    question: 'How do I add my Binance API key?',
    answer:
      'There is a video walkthrough here: https://youtu.be/n2LEik5c7mw',
  },
  {
    question: 'What is an API?',
    answer:
      'An API, or Application Programming Interface, is a secure way for one app to communicate with another. In AlgoTrader, your exchange API key allows the app to read market and account data and, if you permit it, place trades on your behalf without giving the app your exchange password.',
  },
  {
    question: 'Do my funds stay on the exchange?',
    answer:
      'Yes. Your funds always stay on your exchange account. AlgoTrader does not hold your crypto or fiat balance, it just uses your API connection to place trades for you based on your settings and strategy.',
  },
];

export default function FAQ() {
  const [expanded, setExpanded] = React.useState<string | false>('panel0');

  return (
    <Container id="faq" sx={{ py: { xs: 8, sm: 12 } }}>
      <Stack spacing={2} sx={{ maxWidth: 760, mb: 4 }}>
        <Typography
          variant="caption"
          sx={{ color: 'primary.light', letterSpacing: 2, textTransform: 'uppercase' }}
        >
          FAQ
        </Typography>
        <Typography component="h2" variant="h2">
          Questions traders usually ask before connecting an account.
        </Typography>
        <Typography color="text.secondary">
          The site copy is cleaner now, but the important operational details are
          still easy to find.
        </Typography>
      </Stack>
      <Stack spacing={1.25}>
        {faqs.map((faq, index) => (
          <Accordion
            key={faq.question}
            expanded={expanded === `panel${index}`}
            onChange={(_event, isExpanded) =>
              setExpanded(isExpanded ? `panel${index}` : false)
            }
          >
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography component="h3" variant="subtitle2">
                {faq.question}
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Typography variant="body2" sx={{ maxWidth: 900 }}>
                {faq.question === 'How do I get support if I run into an issue?' ? (
                  <>
                    Email <Link href="mailto:support@algo-trader.tech">support@algo-trader.tech</Link>{' '}
                    or reach out through the app. The support path is meant to be
                    direct, not hidden behind a forum.
                  </>
                ) : faq.question === 'How do I add my Binance API key?' ? (
                  <>
                    There is a video walkthrough here:{' '}
                    <Link
                      href="https://youtu.be/n2LEik5c7mw"
                      target="_blank"
                      rel="noreferrer"
                    >
                      https://youtu.be/n2LEik5c7mw
                    </Link>
                  </>
                ) : (
                  faq.answer
                )}
              </Typography>
            </AccordionDetails>
          </Accordion>
        ))}
      </Stack>
    </Container>
  );
}
